import {
  useCallback,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";

import { DottedLabel } from "./overview/DottedLabel";

const TOOLTIP_GAP = 8;
const CARET_SIZE = 6;

type InsightsSectionTitleProps = {
  children: ReactNode;
  tooltip: string;
};

export function InsightsSectionTitle({
  children,
  tooltip,
}: InsightsSectionTitleProps) {
  const tooltipId = useId();
  const [isVisible, setIsVisible] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const triggerRef = useRef<HTMLSpanElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  const updatePosition = useCallback(() => {
    const trigger = triggerRef.current;
    const panel = tooltipRef.current;
    if (!trigger || !panel) {
      return;
    }

    const triggerRect = trigger.getBoundingClientRect();
    const panelRect = panel.getBoundingClientRect();
    const left = triggerRect.left + triggerRect.width / 2 - panelRect.width / 2;
    const top = triggerRect.top - panelRect.height - TOOLTIP_GAP - CARET_SIZE;

    setCoords({ top, left });
  }, []);

  useLayoutEffect(() => {
    if (!isVisible) {
      return;
    }

    updatePosition();
    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);
    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [isVisible, updatePosition]);

  const show = () => setIsVisible(true);
  const hide = () => setIsVisible(false);

  return (
    <>
      <span
        ref={triggerRef}
        className="inline-flex cursor-default text-sm leading-5"
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
        tabIndex={0}
        aria-describedby={isVisible ? tooltipId : undefined}
      >
        <DottedLabel>
          <span className="font-medium">{children}</span>
        </DottedLabel>
      </span>

      {isVisible
        ? createPortal(
            <div
              ref={tooltipRef}
              id={tooltipId}
              role="tooltip"
              className="pointer-events-none fixed z-[700]"
              style={{ top: coords.top, left: coords.left }}
            >
              <div className="text-fs-text-primary-inverse relative max-w-[336px] rounded-[4px] bg-[#333333] px-4 py-3 text-left text-[14px] leading-[20px] font-normal shadow-[0_2px_8px_rgba(0,0,0,0.2)]">
                {tooltip}
                <span
                  aria-hidden
                  className="absolute bottom-[-6px] left-1/2 h-0 w-0 -translate-x-1/2 border-x-[6px] border-t-[6px] border-x-transparent border-t-[#333333]"
                />
              </div>
            </div>,
            document.body
          )
        : null}
    </>
  );
}
